import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  input,
  OnChanges,
  OnInit,
  output,
  Renderer2,
  viewChild,
} from '@angular/core'

import * as d3 from 'd3'
import { debounceTime, filter, Subject, takeUntil, tap } from 'rxjs'

import { DestroyDirective } from '~/core/directives'
import { IChartLineData, ISizeElement } from '~/core/models'
import {
  addInfoTextBlock,
  addInfoValue,
  drawInfoLine,
  drawLine,
  drawPdfLegend,
  drawRectangle,
  forecastNumber,
} from '~/utils/index'

import { IChartEpsSettings } from '../../models'

const MAX_HEIGHT = 500

@Component({
  selector: 'app-eps-pdf-chart',
  template: '<div class="eps-chart" #chart></div>',
  styleUrl: './eps-pdf-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [DestroyDirective],
  standalone: true,
})
export class EpsPdfChartComponent implements OnInit, OnChanges {
  private readonly renderer = inject(Renderer2)
  private readonly destroy$ = inject(DestroyDirective).destroy$
  private readonly drawChart$ = new Subject()

  readonly chartOptions = input<IChartEpsSettings | null>(null)
  readonly sizeChange = output<ISizeElement>()

  readonly chartElement = viewChild<ElementRef>('chart')

  margin = 100
  width = 0
  height = 0

  constructor() {}

  @HostListener('window:resize') onResize(): void {
    this.drawChart$.next(null)
  }

  ngOnChanges(): void {
    this.drawChart$.next(null)
  }

  ngOnInit(): void {
    this.drawChart$
      .pipe(
        debounceTime(100),
        tap(() => this.removeChart()),
        tap(() => this.setSize()),
        filter(() => Boolean(this.chartOptions()?.data.length)),
        tap(() => this.drawChart()),
        takeUntil(this.destroy$)
      )
      .subscribe()
    this.drawChart$.next(null)
  }

  private removeChart(): void {
    const nativeElement = this.chartElement()?.nativeElement

    nativeElement.childNodes.forEach((node: any) => this.renderer.removeChild(nativeElement, node))
  }

  private setSize(): void {
    this.width = this.chartElement()?.nativeElement.offsetWidth
    this.height = Math.min(this.width * 0.375, MAX_HEIGHT)
    this.sizeChange.emit({ width: this.width, height: this.height })
  }

  private drawChart(): void {
    const marginTopContainer = this.margin - 40

    const x1 = this.chartOptions()!.x1
    const x2 = this.chartOptions()!.x2
    const x3 = this.chartOptions()!.x3
    const x4 = this.chartOptions()!.x4

    const data: IChartLineData[] = [...this.chartOptions()!.data]

    const chartElement = this.chartElement()

    for (const child of chartElement?.nativeElement.children) {
      this.renderer.removeChild(chartElement?.nativeElement, child)
    }

    const xMax = this.chartOptions()!.xMax
    const xMin = this.chartOptions()!.xMin
    const yMax = this.chartOptions()!.yMax
    const yMin = this.chartOptions()!.yMin

    const yInfoMax = yMax + yMax * 0.1

    const y = d3.scaleLinear().domain([0, yMax]).range([this.height, 0])
    const x = d3.scaleLinear().domain([xMin, xMax]).range([0, this.width])

    // const yAxis = d3.axisLeft(y)
    const xAxis = d3
      .axisBottom(x)
      .tickFormat((domainValue: any, _index: number) =>
        forecastNumber(domainValue, this.chartOptions()!.numberSettings.toFixed)
      )

    const area = d3
      .area()
      .curve(d3.curveBasis)

      .x((d: any) => {
        return x(d[0])
      })
      .y0(0)

      .y1((d: any) => {
        return y(d[1])
      })

    const line = d3
      .line()
      .curve(d3.curveBasis)

      .x((d: any) => {
        return x(d[0])
      })

      .y((d: any) => {
        return y(d[1])
      })

    const svg = d3
      .select(chartElement?.nativeElement)
      .append('svg')
      .attr('height', this.height + this.margin)
      .attr('width', this.width)
      .attr('padding-top', marginTopContainer)

    const chartGroup = svg.append('g').attr('transform', `translate(${0},${marginTopContainer})`)

    const area0 = data
    const area1 = data.filter(a => a.x >= x1 && a.x <= x4)
    const area2 = data.filter(a => a.x >= x2 && a.x <= x3)

    drawRectangle(
      chartGroup,
      x(xMin),
      y(yMax),
      x(xMax) - x(xMin),
      y(yMin) - y(yMax),
      'var(--proximilar-chart-bg-group1-color)',
      0.2
    )
    drawRectangle(
      chartGroup,
      x(x1),
      y(yMax),
      x(x4) - x(x1),
      y(yMin) - y(yMax),
      'var(--proximilar-chart-bg-group1-color)',
      0.2
    )
    drawRectangle(
      chartGroup,
      x(x2),
      y(yMax),
      x(x3) - x(x2),
      y(yMin) - y(yMax),
      'var(--proximilar-chart-bg-group1-color)',
      0.6
    )

    chartGroup
      .append('path')
      .attr('d', area(area0.map((o: IChartLineData) => [o.x, o.y])))
      .style('fill', 'var(--proximilar-bg-color)')

    chartGroup
      .append('path')
      .attr('d', line(area0.map((o: IChartLineData) => [o.x, o.y])))
      .attr('fill', 'none')
      .attr('stroke', 'var(--proximilar-chart-line-color)')
      .attr('stroke-width', 4)

    const lineSettings = d3
      .line()

      .x((d: any) => {
        return x(d[0])
      })

      .y((d: any) => {
        return y(d[1])
      })

    this.drawConsensusValue(chartGroup, lineSettings, yMax, yMin)
    this.drawAIValue(chartGroup, lineSettings, yMax, yMin, x, y)

    drawInfoLine(
      chartGroup,
      lineSettings([
        [x1, yMin],
        [x1, yInfoMax],
      ])
    )
    drawInfoLine(
      chartGroup,
      lineSettings([
        [x4, yMin],
        [x4, yInfoMax],
      ])
    )
    drawInfoLine(
      chartGroup,
      lineSettings([
        [x1, yInfoMax],
        [x4, yInfoMax],
      ])
    )

    chartGroup
      .append('g')
      .attr('class', 'axis x')
      .attr('transform', `translate(0,${this.height + y(yMin) - y(0)})`)
      .call(xAxis)

    const chartOptions = this.chartOptions()

    addInfoTextBlock(
      chartGroup,
      x,
      y,
      (x1 + x4) / 2,
      y(yMax) - 15,
      x(x4) - x(x1),
      chartOptions!.translates.outcomesFall90 || ''
    )
    addInfoTextBlock(
      chartGroup,
      x,
      y,
      (x2 + x3) / 2,
      (y(yMin) - y(yMax)) / 2,
      x(x3) - x(x2),
      chartOptions!.translates.outcomesFall50 || ''
    )

    // TODO: Don't rerender twice
    if (!area1.length || !area2.length) {
      return
    }

    addInfoValue(chartGroup, '5th pct', x, y, x1, area1[0].y, chartOptions!.numberSettings)
    addInfoValue(chartGroup, '25th pct', x, y, x2, area2[0].y, chartOptions!.numberSettings)
    addInfoValue(
      chartGroup,
      '75th pct',
      x,
      y,
      x3,
      area2[area2.length - 1].y,
      chartOptions!.numberSettings,
      'right'
    )
    addInfoValue(
      chartGroup,
      '95th pct',
      x,
      y,
      x4,
      area1[area1.length - 1].y,
      chartOptions!.numberSettings,
      'right'
    )

    drawPdfLegend(
      chartGroup,
      x,
      y,
      xMax,
      yInfoMax,
      chartOptions!.translates.pdfConsensus || '',
      chartOptions!.translates.pdfForecastProximilar || ''
    )
  }

  private drawConsensusValue(
    chartGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
    lineSettings: d3.Line<[number, number]>,
    yMax: number,
    yMin: number
  ): void {
    drawLine(
      chartGroup,
      lineSettings([
        [this.chartOptions()!.pdfConsensus, yMin],
        [this.chartOptions()!.pdfConsensus, yMax],
      ]),
      'var(--proximilar-chart-info1-color)',
      4
    )
  }

  private drawAIValue(
    chartGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
    lineSettings: d3.Line<[number, number]>,
    yMax: number,
    yMin: number,
    x: d3.ScaleLinear<number, number, never>,
    y: d3.ScaleLinear<number, number, never>
  ): void {
    const chartOptions = this.chartOptions()

    drawLine(
      chartGroup,
      lineSettings([
        [chartOptions!.pdfForecastProximilar, yMin],
        [chartOptions!.pdfForecastProximilar, yMax],
      ]),
      'var(--proximilar-chart-info2-color)',
      4
    )

    addInfoValue(
      chartGroup,
      '',
      x,
      y,
      chartOptions!.pdfForecastProximilar,
      yMax,
      chartOptions!.numberSettings,
      'right',
      'var(--proximilar-chart-info2-color)'
    )
  }
}
